import type { BuildReorderRequest, BuildReorderResponse } from '@palaccount/contracts';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/api';
import { throwApiError } from '@/lib/server-messages';

import { PURCHASE_ORDERS_KEY } from './api';

/** Wave 5A — the reorder builder's data layer, the purchases shape. */

export const REORDER_KEY = ['reorder-suggestions'] as const;

export interface ReorderFilters {
  warehouseId: string;
  categoryId: string;
  manufacturerId: string;
  supplierId: string | null;
  belowMinimumOnly: boolean;
  includeZero: boolean;
  q: string;
}

export const DEFAULT_REORDER_FILTERS: ReorderFilters = {
  warehouseId: '',
  categoryId: '',
  manufacturerId: '',
  supplierId: null,
  belowMinimumOnly: true,
  includeZero: false,
  q: '',
};

export function useReorderSuggestions(filters: ReorderFilters, page: number, pageSize: number) {
  return useQuery({
    queryKey: [...REORDER_KEY, filters, page, pageSize],
    queryFn: async () => {
      const response = await api.reorder.suggestions({
        query: {
          ...(filters.warehouseId ? { warehouseId: filters.warehouseId } : {}),
          ...(filters.categoryId ? { categoryId: filters.categoryId } : {}),
          ...(filters.manufacturerId ? { manufacturerId: filters.manufacturerId } : {}),
          ...(filters.supplierId ? { supplierId: filters.supplierId } : {}),
          belowMinimumOnly: filters.belowMinimumOnly ? ('true' as const) : ('false' as const),
          includeZero: filters.includeZero ? ('true' as const) : ('false' as const),
          ...(filters.q.trim() ? { q: filters.q.trim() } : {}),
          page,
          pageSize,
        },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    placeholderData: keepPreviousData,
  });
}

/** The build: a PUR-01 draft from the ticked lines; the orders register refreshes. */
export function useBuildReorder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: BuildReorderRequest): Promise<BuildReorderResponse> => {
      const response = await api.reorder.build({ body });
      if (response.status !== 201) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: PURCHASE_ORDERS_KEY });
    },
  });
}
